import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Box, Grid, IconButton } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import InfrastructurePillarBox from "./InfrastructurePillarBox";

export default function InfrastructurePillarsScoreChart({
  pillarScores,
  height,
  shouldBeLoaded,
  link = "",
  showTitles = true,
  sx = {},
  trendData = {},
}) {
  const colors = useColors();
  const [loading, setLoading] = useState(true);
  const scrollContainer = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Sort pillars by coverage and generate chart options
  const { sortedPillars, chartOptions } = useMemo(() => {
    if (Object.keys(pillarScores).length === 0) {
      return { sortedPillars: [], chartOptions: {} };
    }

    // Sort pillars by coverage (lowest first)
    const sorted = Object.entries(pillarScores)
      .map(([pillar, data]) => ({ pillar, ...data }))
      .sort((a, b) => a.coverage - b.coverage);

    // Generate chart options for each pillar
    const options = {};
    sorted.forEach(({ pillar, coverage }) => {
      options[pillar] = {
        series: [coverage],
        colors: [colors.primary_300main],
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val}%`,
          style: {
            fontSize: "12px",
            fontFamily: "Poppins",
            fontWeight: "400",
          },
        },
        stroke: { lineCap: "round", width: 2, colors: [colors.grey_300] },
        fill: { type: "solid" },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            track: {
              background: colors.grey_200,
              strokeWidth: "100%",
              margin: 0,
              dropShadow: { enabled: false },
            },
            hollow: { margin: 0, size: "65%", background: "transparent" },
            dataLabels: {
              name: { show: false },
              value: {
                offsetY: 0,
                fontSize: "18px",
                fontFamily: "Poppins",
                fontWeight: "600",
              },
            },
          },
        },
      };
    });

    return { sortedPillars: sorted, chartOptions: options };
  }, [pillarScores, colors.primary_300main, colors.grey_300, colors.grey_200]);

  // Handle loading state and arrow visibility
  useEffect(() => {
    if (shouldBeLoaded || sortedPillars.length > 0) {
      setLoading(false);
    }
  }, [shouldBeLoaded, sortedPillars.length]);

  useEffect(() => {
    const container = scrollContainer.current;

    const updateArrowVisibility = () => {
      if (!container) return;

      const hasOverflow = container.scrollWidth > container.offsetWidth;
      if (!hasOverflow) {
        setShowLeftArrow(false);
        setShowRightArrow(false);
      } else {
        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(
          container.scrollLeft <
            container.scrollWidth - container.offsetWidth - 10
        );
      }
    };

    if (container && !loading) {
      const timeoutId = setTimeout(updateArrowVisibility, 100);
      container.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);

      return () => {
        clearTimeout(timeoutId);
        container.removeEventListener("scroll", updateArrowVisibility);
        window.removeEventListener("resize", updateArrowVisibility);
      };
    }
  }, [loading, sortedPillars.length]);

  const scroll = (scrollOffset) => {
    const container = scrollContainer.current;
    if (!container) return;

    container.scrollLeft += scrollOffset;
  };

  return (
    <>
      {/* Chart part */}
      {loading ? (
        <Grid item>
          <Grid
            container
            height="240px"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingIcon />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "140px",
            gap: 1,
          }}
        >
          {/* Left Arrow - positioned outside chart area */}
          {showLeftArrow ? (
            <IconButton
              onClick={() => scroll(-180)}
              sx={{
                flexShrink: 0,
                width: "32px",
                height: "32px",
              }}
            >
              <ArrowBackIosNewRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: "32px", flexShrink: 0 }} /> // Placeholder to maintain spacing
          )}

          {/* Chart Container */}
          <Box
            ref={scrollContainer}
            className="scroll-container"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              alignItems: "center",
              overflowX: "scroll",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "-ms-overflow-style": "none",
              scrollbarWidth: "none",
            }}
          >
            {sortedPillars.map(({ pillar, coverage, domainsCount }) => (
              <Box
                key={pillar}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <InfrastructurePillarBox
                  pillar={pillar}
                  coverage={coverage}
                  domainsCount={domainsCount}
                  chartOptions={chartOptions[pillar]}
                  trendData={trendData[pillar]}
                />
              </Box>
            ))}
          </Box>

          {/* Right Arrow - positioned outside chart area */}
          {showRightArrow ? (
            <IconButton
              onClick={() => scroll(180)}
              sx={{
                flexShrink: 0,
                width: "32px",
                height: "32px",
              }}
            >
              <ArrowForwardIosRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: "32px", flexShrink: 0 }} /> // Placeholder to maintain spacing
          )}
        </Box>
      )}
    </>
  );
}
